import type { StatsDto } from "@clinic/shared";
import { useQuery } from "@tanstack/react-query";
import { useId, useState } from "react";
import { Navigate } from "react-router";
import { api } from "../api";
import { addDays, formatDate, formatMoney, localDate, useT } from "../i18n";
import { canManage } from "../session";
import { Alert, Button, Card, PageHeader, Spinner } from "../ui";
import { useClinic, useRequiredMe } from "./app";

type Range = "week" | "month" | "30" | "90";

function rangeDates(range: Range, today: string): { from: string; to: string } {
  const [y, m, d] = today.split("-").map(Number);
  if (range === "week") {
    const weekday = new Date(Date.UTC(y!, m! - 1, d!)).getUTCDay() || 7;
    const from = addDays(today, 1 - weekday);
    return { from, to: addDays(from, 6) };
  }
  if (range === "month") {
    const from = `${today.slice(0, 7)}-01`;
    const lastDay = new Date(Date.UTC(y!, m!, 0)).getUTCDate();
    return { from, to: `${today.slice(0, 7)}-${String(lastDay).padStart(2, "0")}` };
  }
  return { from: addDays(today, -(Number(range) - 1)), to: today };
}

const compact = (n: number) =>
  new Intl.NumberFormat(undefined, { notation: n >= 10_000 ? "compact" : "standard", maximumFractionDigits: 1 }).format(
    n,
  );

/** Clinic statistics (CB-25). Owner/admin only, since it shows revenue. */
export function InsightsPage() {
  const me = useRequiredMe();
  const { t, tError, locale } = useT();
  const clinic = useClinic();
  const [range, setRange] = useState<Range>("30");
  const today = localDate(Date.now(), clinic.data?.timezone);
  const { from, to } = rangeDates(range, today);
  const stats = useQuery({
    queryKey: ["stats", from, to],
    enabled: clinic.isSuccess,
    queryFn: () => api<StatsDto>("GET", `/stats?from=${from}&to=${to}`),
  });
  if (!canManage(me)) return <Navigate to="/app" replace />;

  const ranges: Range[] = ["week", "month", "30", "90"];
  const money = (cents: number) => formatMoney(locale, cents, stats.data?.currency ?? "DKK");

  return (
    <div className="space-y-6">
      <PageHeader title={t("insights.title")} subtitle={t("insights.subtitle")} />

      {/* Filters: one row above the charts. */}
      <div className="flex flex-wrap gap-2">
        {ranges.map((r) => (
          <Button key={r} type="button" variant={range === r ? "primary" : "secondary"} onClick={() => setRange(r)}>
            {t(`insights.range_${r}`)}
          </Button>
        ))}
      </div>

      {stats.isPending || clinic.isPending ? (
        <div className="flex justify-center p-8 text-brand-700">
          <Spinner />
        </div>
      ) : stats.isError ? (
        <Alert>{tError(stats.error)}</Alert>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-3">
            <StatTile
              label={t("insights.bookings")}
              value={compact(stats.data.bookings.total - stats.data.bookings.cancelled)}
            />
            <StatTile label={t("insights.completed")} value={compact(stats.data.bookings.completed)} />
            <StatTile
              label={`${t("insights.cancelled")} / ${t("insights.no_show")}`}
              value={`${compact(stats.data.bookings.cancelled)} / ${compact(stats.data.bookings.noShow)}`}
            />
            <StatTile label={t("insights.revenue")} value={money(stats.data.revenue.completedCents)} />
            <StatTile label={t("insights.expected")} value={money(stats.data.revenue.expectedCents)} />
            <StatTile
              label={t("insights.online_share")}
              value={
                stats.data.bookings.total
                  ? `${Math.round((stats.data.bookings.online / stats.data.bookings.total) * 100)}%`
                  : "–"
              }
            />
          </div>

          <Card>
            <h2 className="font-semibold">{t("insights.by_day")}</h2>
            {stats.data.byDay.every((d) => d.bookings === 0) ? (
              <p className="mt-4 text-sm text-slate-500">{t("insights.empty")}</p>
            ) : (
              <ColumnChart days={stats.data.byDay} />
            )}
          </Card>

          <Card className="p-0">
            <div className="px-6 pt-5">
              <h2 className="font-semibold">{t("insights.by_practitioner")}</h2>
              <p className="mt-1 text-sm text-slate-600">{t("insights.by_practitioner_hint")}</p>
            </div>
            <table className="mt-4 w-full text-sm">
              <thead className="text-left text-xs tracking-wide text-slate-500 uppercase">
                <tr>
                  <th className="px-6 py-2 font-medium">{t("bookings.practitioner")}</th>
                  <th className="px-3 py-2 text-right font-medium">{t("insights.bookings")}</th>
                  <th className="px-3 py-2 text-right font-medium">{t("insights.booked")}</th>
                  <th className="px-3 py-2 text-right font-medium">{t("insights.scheduled")}</th>
                  <th className="px-6 py-2 font-medium">{t("insights.utilization")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {stats.data.byPractitioner.map((p) => (
                  <tr key={p.id}>
                    <td className="px-6 py-3 font-medium">{p.name}</td>
                    <td className="px-3 py-3 text-right tabular-nums">{p.bookings}</td>
                    <td className="px-3 py-3 text-right tabular-nums">{t("common.minutes", { n: p.bookedMinutes })}</td>
                    <td className="px-3 py-3 text-right tabular-nums">
                      {t("common.minutes", { n: p.scheduledMinutes })}
                    </td>
                    <td className="px-6 py-3">
                      {p.utilization === null ? (
                        <span className="text-slate-500">{t("insights.no_hours")}</span>
                      ) : (
                        <Meter value={p.utilization} />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>

          <Card className="p-0">
            <h2 className="px-6 pt-5 font-semibold">{t("insights.by_service")}</h2>
            {stats.data.byService.length === 0 ? (
              <p className="p-6 text-sm text-slate-500">{t("insights.empty")}</p>
            ) : (
              <table className="mt-4 w-full text-sm">
                <thead className="text-left text-xs tracking-wide text-slate-500 uppercase">
                  <tr>
                    <th className="px-6 py-2 font-medium">{t("bookings.service")}</th>
                    <th className="px-3 py-2 text-right font-medium">{t("insights.bookings")}</th>
                    <th className="px-6 py-2 text-right font-medium">{t("insights.revenue")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {stats.data.byService.map((s) => (
                    <tr key={s.id}>
                      <td className="px-6 py-3 font-medium">{s.name}</td>
                      <td className="px-3 py-3 text-right tabular-nums">{s.bookings}</td>
                      <td className="px-6 py-3 text-right tabular-nums">{money(s.revenueCents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </>
      )}
    </div>
  );
}

function StatTile({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <p className="text-sm text-slate-500">{label}</p>
      <p className="mt-2 text-3xl font-semibold text-slate-900">{value}</p>
    </Card>
  );
}

/** Utilization meter: the unfilled track is a lighter step of the same hue. */
function Meter({ value }: { value: number }) {
  const pct = Math.round(value * 100);
  return (
    <div className="flex items-center gap-3">
      <div className="h-2 w-full max-w-40 overflow-hidden rounded-full bg-brand-100" aria-hidden="true">
        <div className="h-full rounded-full bg-brand-700" style={{ width: `${pct}%` }} />
      </div>
      <span className="w-10 text-right text-sm tabular-nums">{pct}%</span>
    </div>
  );
}

/**
 * Bookings per day as a single-series column chart: one hue, thin columns with a
 * rounded cap, hairline gridlines, a per-column hover tooltip and a table view.
 */
function ColumnChart({ days }: { days: StatsDto["byDay"] }) {
  const { t, locale } = useT();
  const [hover, setHover] = useState<number | null>(null);
  const [showTable, setShowTable] = useState(false);
  const titleId = useId();
  const width = 720;
  const height = 220;
  const pad = { top: 16, right: 12, bottom: 28, left: 32 };
  const max = Math.max(1, ...days.map((d) => d.bookings));
  const step = max <= 5 ? 1 : max <= 10 ? 2 : max <= 25 ? 5 : 10;
  const top = Math.ceil(max / step) * step;
  const innerW = width - pad.left - pad.right;
  const innerH = height - pad.top - pad.bottom;
  const band = innerW / days.length;
  const barW = Math.min(24, band * 0.7);
  const y = (v: number) => pad.top + innerH - (v / top) * innerH;
  const ticks = Array.from({ length: top / step + 1 }, (_, i) => i * step);
  const labelEvery = days.length > 14 ? Math.ceil(days.length / 7) : 1;
  const dayLabel = (date: string, long = false) =>
    formatDate(
      locale,
      `${date}T12:00:00Z`,
      "UTC",
      long ? { year: "numeric" } : { weekday: "short", day: "numeric", month: undefined },
    );

  return (
    <div className="mt-4">
      <div className="relative">
        <svg viewBox={`0 0 ${width} ${height}`} className="w-full" role="img" aria-labelledby={titleId}>
          <title id={titleId}>{t("insights.by_day")}</title>
          {ticks.map((v) => (
            <g key={v}>
              <line x1={pad.left} x2={width - pad.right} y1={y(v)} y2={y(v)} stroke="#e7e5e4" strokeWidth={1} />
              <text
                x={pad.left - 6}
                y={y(v) + 4}
                textAnchor="end"
                fontSize={11}
                fill="#78716c"
                style={{ fontVariantNumeric: "tabular-nums" }}
              >
                {v}
              </text>
            </g>
          ))}
          {days.map((d, i) => {
            const x = pad.left + band * i + (band - barW) / 2;
            const h = Math.max(0, pad.top + innerH - y(d.bookings));
            const r = Math.min(4, h);
            const path =
              h === 0
                ? ""
                : `M${x},${pad.top + innerH} v${-(h - r)} a${r},${r} 0 0 1 ${r},${-r} h${barW - 2 * r} a${r},${r} 0 0 1 ${r},${r} v${h - r} z`;
            return (
              // biome-ignore lint/a11y/noStaticElementInteractions: hover/focus mirror the tooltip of a keyboard-reachable group
              <g
                key={d.date}
                tabIndex={0}
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover(null)}
                onFocus={() => setHover(i)}
                onBlur={() => setHover(null)}
                aria-label={`${dayLabel(d.date, true)}: ${d.bookings}`}
                style={{ outline: "none" }}
              >
                {/* Hit target wider than the mark. */}
                <rect x={pad.left + band * i} y={pad.top} width={band} height={innerH} fill="transparent" />
                {h > 0 ? <path d={path} fill={hover === i ? "#115e59" : "#0f766e"} /> : null}
                {i % labelEvery === 0 ? (
                  <text x={x + barW / 2} y={height - 8} textAnchor="middle" fontSize={11} fill="#78716c">
                    {dayLabel(d.date)}
                  </text>
                ) : null}
              </g>
            );
          })}
        </svg>
        {hover !== null && days[hover] ? (
          <div
            className="pointer-events-none absolute top-0 rounded-lg bg-slate-900 px-3 py-2 text-xs text-white shadow"
            style={{ left: `${((pad.left + band * hover + band / 2) / width) * 100}%`, transform: "translateX(-50%)" }}
          >
            <span className="font-semibold">{days[hover].bookings}</span> {t("insights.bookings").toLowerCase()}
            <span className="block text-slate-300">{dayLabel(days[hover].date, true)}</span>
          </div>
        ) : null}
      </div>
      <button
        type="button"
        className="mt-2 text-sm font-medium text-brand-700 hover:underline"
        onClick={() => setShowTable((v) => !v)}
      >
        {showTable ? t("common.close") : t("insights.by_day")} ▾
      </button>
      {showTable ? (
        <table className="mt-2 w-full text-sm">
          <tbody className="divide-y divide-slate-200">
            {days.map((d) => (
              <tr key={d.date}>
                <td className="py-1.5">{dayLabel(d.date, true)}</td>
                <td className="py-1.5 text-right tabular-nums">{d.bookings}</td>
                <td className="py-1.5 text-right tabular-nums text-slate-500">
                  {formatMoney(locale, d.revenueCents, "DKK")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </div>
  );
}
